import React from 'react'
import { CategoryIconProps, CategorySvgIcon } from '../icons/CategorySvgIcon'
import { mfLogoBaseUrl } from '../env'
import { toPascalCaseWithSpaces } from '../utils/utils'

/**
 * Single source of truth for category configuration
 * Maps each enum to its label and description
 * Icon base name is generated dynamically from the enum name (matching Java's assetName() logic)
 */
export interface CategoryConfig {
  label: string
  description: string
}

export const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  // Equity Funds - Market Capitalisation
  LARGE_CAP: {
    label: 'Large Cap',
    description: 'Top 100 companies with stable growth'
  },
  MID_CAP: {
    label: 'Mid Cap',
    description: 'Next 150 emerging mid-sized companies'
  },
  SMALL_CAP: {
    label: 'Small Cap',
    description: 'Small-scale companies outside Top 250'
  },
  LARGE_AND_MID_CAP: {
    label: 'Large & Mid Cap',
    description: 'Stability and growth, Top 250 companies'
  },

  // Equity Funds - Diversification
  MULTI_CAP: {
    label: 'Multi Cap',
    description: 'All market caps, broad market exposure'
  },
  FLEXI_CAP: {
    label: 'Flexi Cap',
    description: 'Strategic mix of large, mid and small cap'
  },
  FOCUSED: {
    label: 'Focused',
    description: 'Max 30 stocks, a concentrated approach'
  },
  FOCUSED_FUND: {
    label: 'Focused',
    description: 'Max 30 stocks, a concentrated approach'
  },
  ELSS_TAX_SAVINGS: {
    label: 'ELSS',
    description: 'Equity-Linked Savings Scheme. Save taxes'
  },
  DIVIDEND_YIELD: {
    label: 'Dividend Yield',
    description: 'Extra wealth via stocks giving dividends'
  },
  GLOBAL_EMERGING_MARKETS: {
    label: 'Emerging Markets',
    description: ''
  },
  GLOBAL_DIVERSIFIED_ALL_COUNTRIES: {
    label: 'Diversified All Countries',
    description: ''
  },
  GLOBAL_INNOVATION_AND_DISRUPTION: {
    label: 'Innovation & Disruption',
    description: ''
  },
  GLOBAL_DEVELOPED_MARKETS: {
    label: 'Developed Markets',
    description: ''
  },
  GLOBAL_US_NASDAQ_100: {
    label: 'US NASDAQ 100',
    description: ''
  },
  GLOBAL_US_DIVERSIFIED_EQUITY: {
    label: 'US Diversified Equity',
    description: ''
  },
  VALUE_AND_CONTRA: {
    label: 'Value & Contra',
    description: 'Undervalued stocks with growth potential'
  },

  // Sectoral/Thematic
  SECTORAL_THEMATIC_AUTO: {
    label: 'Auto',
    description: 'Cars, bikes and automobile stocks'
  },
  SECTORAL_THEMATIC_COMMODITY: {
    label: 'Commodity',
    description: 'Metals and raw material stocks'
  },
  SECTORAL_THEMATIC_CONSUMPTION: {
    label: 'Consumption',
    description: 'Consumer spending-driven stocks'
  },
  SECTORAL_THEMATIC_DEFENCE: {
    label: 'Defence',
    description: 'Military and security company stocks'
  },
  SECTORAL_THEMATIC_ENERGY: {
    label: 'Energy',
    description: 'Power, oil, gas and energy stocks '
  },
  SECTORAL_THEMATIC_ESG: {
    label: 'ESG',
    description: 'Sustainable, ethical company stocks'
  },
  SECTORAL_THEMATIC_EXPORT_AND_SERVICES: {
    label: 'Export & Services',
    description: 'Trade and services company stocks'
  },
  SECTORAL_THEMATIC_FINANCIAL_SERVICES: {
    label: 'Financial Services',
    description: 'Banks, NBFCs and fintech stocks'
  },
  SECTORAL_THEMATIC_FMCG: {
    label: 'FMCG',
    description: 'Essential, daily product company stocks'
  },
  SECTORAL_THEMATIC_HEALTHCARE: {
    label: 'Healthcare',
    description: 'Healthcare and pharma stocks'
  },
  SECTORAL_THEMATIC_HOUSING: {
    label: 'Housing',
    description: 'Home building, real estate company stocks'
  },
  SECTORAL_THEMATIC_INFRASTRUCTURE: {
    label: 'Infrastructure',
    description: 'Raw material and construction stocks'
  },
  SECTORAL_THEMATIC_IPO: {
    label: 'IPO',
    description: 'Newly listed, upcoming company stocks'
  },
  SECTORAL_THEMATIC_MANUFACTURING: {
    label: 'Manufacturing',
    description: 'Factory and industrial company stocks'
  },
  SECTORAL_THEMATIC_MNC: {
    label: 'MNC',
    description: 'Big, reputed global company stocks'
  },
  SECTORAL_THEMATIC_OTHERS: {
    label: 'Others',
    description: 'Funds outside the standard categories'
  },
  SECTORAL_THEMATIC_PSU: {
    label: 'PSU',
    description: 'Government-owned company stocks'
  },
  SECTORAL_THEMATIC_QUANT: {
    label: 'Quant',
    description: 'Data-driven strategy based stocks'
  },
  SECTORAL_THEMATIC_TECHNOLOGY: {
    label: 'Technology',
    description: 'IT, software and tech stocks'
  },
  SECTORAL_THEMATIC_TRANSPORT_AND_LOGISTICS: {
    label: 'Transport & Logistics',
    description: 'Shipping and transport company stocks'
  },

  // Index Funds
  INDEX_AUTO: {
    label: 'Auto',
    description: 'Based on auto sector indices'
  },
  INDEX_DEFENCE: {
    label: 'Defence',
    description: 'Based on defense sector indices'
  },
  INDEX_HOUSING: {
    label: 'Housing',
    description: 'Based on housing sector indices'
  },
  INDEX_LARGE_AND_MID_CAP: {
    label: 'Large & Mid Cap',
    description: "Based on India's large & mid cap indices"
  },
  INDEX_LARGE_CAP: {
    label: 'Large Cap',
    description: "Based on India's large cap indices"
  },
  INDEX_LARGE_CAP_EQUAL_WEIGHT: {
    label: 'Large Cap - Equal Weight',
    description: "Based on India's equal weight indices"
  },
  INDEX_LARGE_CAP_NEXT_FIFTY: {
    label: 'Large Cap - Next 50',
    description: 'Invests in indices like Nifty Next 50'
  },
  INDEX_LOW_VOLATILITY: {
    label: 'Low Volatility',
    description: 'Indices like Nifty Low Volatility 50'
  },
  INDEX_MANUFACTURING: {
    label: 'Manufacturing',
    description: 'Based on manufacturing sector indices'
  },
  INDEX_MICROCAP: {
    label: 'Micro Cap',
    description: "Based on India's micro cap indices"
  },
  INDEX_MOMENTUM: {
    label: 'Momentum',
    description: 'Indices like Nifty 200 Momentum 30'
  },
  INDEX_MULTIFACTOR: {
    label: 'Multifactor',
    description: 'Indices like NIFTY Alpha Quality Value Low-Volatility 30'
  },
  INDEX_OTHERS: {
    label: 'Other Index Funds',
    description: 'Funds outside the standard categories'
  },
  INDEX_REALTY: {
    label: 'Realty',
    description: 'Realty indices like Nifty Realty'
  },
  INDEX_MID_CAP: {
    label: 'Mid Cap',
    description: "Based on India's mid cap indices"
  },
  INDEX_PSU: {
    label: 'PSU',
    description: 'Based on PSU sector indices'
  },
  INDEX_SMALL_CAP: {
    label: 'Small Cap',
    description: "Based on India's small cap indices"
  },
  INDEX_TECHNOLOGY: {
    label: 'Technology',
    description: 'Based on technology sector indices'
  },
  INDEX_TOTAL_MARKET: {
    label: 'Total Market',
    description: "Based on India's total market indices"
  },
  INDEX_TOURISM: {
    label: 'Tourism',
    description: 'Based on tourism sector indices'
  },
  INDEX_BANKS: {
    label: 'Banks',
    description: 'Banks, NBFCs indices like Nifty Bank'
  },
  INDEX_VALUE: {
    label: 'Value',
    description: 'Indices like Nifty 50 Value 20'
  },

  // Hybrid Funds
  AGGRESSIVE_ALLOCATION: {
    label: 'Aggressive Hybrid',
    description: 'Invests upto 80% in stocks, 20% in bonds'
  },
  BALANCED_ALLOCATION: {
    label: 'Balanced Hybrid',
    description: 'Invests upto 50:50 in stocks and bonds'
  },
  CONSERVATIVE_ALLOCATION: {
    label: 'Conservative Hybrid',
    description: 'Invests only 10% in stocks, 90% in bonds'
  },
  CHILDREN: {
    label: 'Children',
    description: "Invests for children's future goals"
  },
  DYNAMIC_ASSET_ALLOCATION: {
    label: 'Dynamic Asset Allocation',
    description: 'Invests in equity/bonds aligned to markets'
  },
  EQUITY_SAVINGS: {
    label: 'Equity Savings Hybrid',
    description: 'Invests in equity, bonds and arbitrage'
  },
  FUND_OF_FUNDS: {
    label: 'Fund of Funds (FoF)',
    description: 'Invests in multiple other mutual funds'
  },
  MULTI_ASSET_ALLOCATION: {
    label: 'Multi-Asset Allocation',
    description: 'Invests in equity, bonds, gold, and more'
  },
  RETIREMENT: {
    label: 'Retirement',
    description: 'Invests towards your retirement corpus'
  },
  ARBITRAGE_FUND: {
    label: 'Arbitrage',
    description: 'Low-risk returns from market price gaps'
  },
  COMMODITY_GOLD: {
    label: 'Gold Funds',
    description: 'Invests in gold ETFs to mirror gold prices'
  },
  COMMODITY_SILVER: {
    label: 'Silver Funds',
    description: 'Invests in silver ETFs to mirror silver prices'
  },
  COMMODITY_GOLD_AND_SILVER: {
    label: 'Gold & Silver Funds',
    description: 'Exposure to gold & silver from one fund'
  }
}

/**
 * Get category configuration for a given enum
 */
export function getCategoryConfig(categoryId: string): CategoryConfig | undefined {
  return CATEGORY_CONFIG[categoryId]
}

/**
 * Get label for a category enum
 * For index funds (except INDEX_OTHERS), appends " Index" to the label (matching Java's FundCategoryUtil.label())
 */
export function getCategoryLabel(categoryId: string): string {
  const config = CATEGORY_CONFIG[categoryId]
  const fallbackLabel = () => {
    if (categoryId.startsWith('GLOBAL_')) {
      return toPascalCaseWithSpaces(categoryId.replace(/^GLOBAL_/, ''))
    }
    return toPascalCaseWithSpaces(categoryId)
  }

  if (!config) return fallbackLabel()

  const baseLabel = config?.label ?? fallbackLabel()

  // Handle edge case: INDEX_OTHERS should not have " Index" appended
  if (categoryId === 'INDEX_OTHERS') {
    return baseLabel
  }

  // Append " Index" if "index" is present in the enum name (matching Java logic)
  if (categoryId.toLowerCase().includes('index')) {
    return `${baseLabel} Index`
  }

  return baseLabel
}

/**
 * Get description for a category enum
 */
export function getCategoryDescription(categoryId: string): string {
  return CATEGORY_CONFIG[categoryId]?.description || ''
}

/**
 * Generate asset name from category enum (matching Java's assetName() logic)
 * Removes prefixes like "sectoral_thematic_", "index_", "equity_"
 * Converts to lowercase, replaces "&" with "and", spaces with "_"
 */
function generateAssetName(categoryId: string): string {
  let assetName = categoryId.toLowerCase()

  // Remove prefixes
  if (assetName.includes('sectoral_thematic_')) {
    assetName = assetName.replace(/sectoral_thematic_/g, '')
  }
  if (assetName.includes('index_')) {
    assetName = assetName.replace(/index_/g, '')
  }
  if (assetName.includes('equity_')) {
    assetName = assetName.replace(/equity_/g, '')
  }

  // Replace "&" with "and" and spaces with "_"
  return assetName.replace(/&/g, 'and').replace(/\s+/g, '_')
}

/**
 * Get icon base name for a category enum
 * Always generates from enum name (matching Java's assetName() logic)
 */
export function getCategoryIconBaseName(categoryId: string): string {
  return generateAssetName(categoryId)
}

/**
 * Get icon component for a category enum
 * Uses the backend image path pattern: /images/mutual_fund/categories/v2/{baseName}.svg
 */
export function getCategoryIcon(categoryId: string): React.FC<CategoryIconProps> {
  const iconBaseName = getCategoryIconBaseName(categoryId)

  const IconComponent: React.FC<CategoryIconProps> = (props) =>
    React.createElement(CategorySvgIcon, {
      baseName: iconBaseName,
      ...props,
      imageBasePath: `${mfLogoBaseUrl}/images/mutual_fund/categories/v2`
    })

  return IconComponent
}

/**
 * Preload all category assets (normal and filled versions) for all categories
 * This function preloads images to improve performance when categories are displayed
 * @param onComplete - Optional callback when all images are preloaded
 * @param onError - Optional error handler for failed image loads
 */
export function preloadAllCategoryAssets(
  onComplete?: () => void,
  onError?: (error: Error, categoryId: string, assetUrl: string) => void
): void {
  const imageBasePath = `${mfLogoBaseUrl}/images/mutual_fund/categories/v2`
  const categoryIds = Object.keys(CATEGORY_CONFIG)
  let loadedCount = 0
  let errorCount = 0
  const totalImages = categoryIds.length * 2 // Each category has normal and filled versions

  // Helper function to check if all images are done loading (success or error)
  const checkCompletion = () => {
    if (loadedCount + errorCount === totalImages && onComplete) {
      onComplete()
    }
  }

  // Create Image objects for all assets
  categoryIds.forEach((categoryId) => {
    const baseName = getCategoryIconBaseName(categoryId)
    const normalUrl = `${imageBasePath}/${baseName}.svg`
    const filledUrl = `${imageBasePath}/${baseName}_filled.svg`

    // Preload normal version
    const normalImg = new Image()
    normalImg.onload = () => {
      loadedCount++
      checkCompletion()
    }
    normalImg.onerror = () => {
      errorCount++
      if (onError) {
        onError(new Error(`Failed to load normal asset for ${categoryId}`), categoryId, normalUrl)
      }
      checkCompletion()
    }
    normalImg.src = normalUrl

    // Preload filled version
    const filledImg = new Image()
    filledImg.onload = () => {
      loadedCount++
      checkCompletion()
    }
    filledImg.onerror = () => {
      errorCount++
      if (onError) {
        onError(new Error(`Failed to load filled asset for ${categoryId}`), categoryId, filledUrl)
      }
      checkCompletion()
    }
    filledImg.src = filledUrl
  })

  // If no images to load, call onComplete immediately
  if (totalImages === 0 && onComplete) {
    onComplete()
  }
}
